import React, { useEffect, useRef, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import api, { fileUrl } from "../lib/api";
import { MessageCircle, X, Send } from "lucide-react";

export const ChatWidget = () => {
  const { t, guestId, lang } = useApp();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);
  const params = { thread_type: "general", guest_id: guestId };

  const load = useCallback(async () => {
    try {
      const data = await api.getChat(params);
      setMessages(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestId]);

  const loadUnread = useCallback(async () => {
    try {
      const d = await api.unread({ guest_id: guestId });
      setUnread(d?.count || 0);
    } catch { /* ignore */ }
  }, [guestId]);

  useEffect(() => {
    loadUnread();
    const id = setInterval(loadUnread, 12000);
    return () => clearInterval(id);
  }, [loadUnread]);

  useEffect(() => {
    if (!open) return;
    load();
    setUnread(0);
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [open, load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const val = text.trim();
    if (!val) return;
    setText("");
    const optimistic = { message_id: "tmp" + Date.now(), sender: "customer", text: val, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    try {
      await api.sendChat({ thread_type: "general", guest_id: guestId, text: val, display_name: t.you });
      load();
    } catch { /* ignore */ }
  };

  return (
    <>
      <button
        data-testid="chat-toggle-button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 end-5 z-40 h-14 w-14 rounded-2xl bg-primary text-primary-foreground shadow-xl shadow-primary/30 grid place-items-center hover:scale-105 transition-transform"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        {!open && unread > 0 && (
          <span className="absolute -top-1 -end-1 h-5 min-w-5 px-1 rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold grid place-items-center">{unread}</span>
        )}
      </button>

      {open && (
        <div data-testid="chat-panel" className="fixed bottom-24 end-5 z-40 w-[calc(100vw-2.5rem)] max-w-sm h-[28rem] rounded-3xl bg-card border border-border shadow-2xl flex flex-col overflow-hidden animate-ha-in">
          <div className="p-4 bg-primary text-primary-foreground">
            <div className="font-head font-bold">{t.chatTitle}</div>
            <div className="text-xs opacity-80">{t.brand} · Norrköping</div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar bg-background">
            {messages.length === 0 && (
              <div className="text-center text-sm text-muted-foreground py-6">{t.chatEmpty}</div>
            )}
            {messages.map((m) => {
              const mine = m.sender === "customer";
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-primary text-primary-foreground rounded-ee-sm" : "bg-card border border-border rounded-es-sm"}`}>
                    {m.text}
                    {m.image_path && (
                      <img src={fileUrl(m.image_path)} alt="" className="mt-1 rounded-lg max-h-40" />
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
          <div className="p-3 border-t border-border flex items-center gap-2 bg-card">
            <input
              data-testid="chat-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={t.chatPlaceholder}
              className="flex-1 h-11 rounded-xl border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            />
            <button data-testid="chat-send-button" onClick={send} className="h-11 w-11 rounded-xl bg-primary text-primary-foreground grid place-items-center hover:opacity-90">
              <Send className="h-4 w-4 rtl:rotate-180" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
